import React from "react";
import { cva } from "class-variance-authority";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { Icon } from "../../atoms/Icon/Icon";
import { Text } from "../../atoms/Text/Text";
import { cn } from "../../lib/cn";

type Status = "success" | "warning" | "error" | "info";

const banner = cva("flex items-start gap-3 rounded-md border p-4", {
  variants: {
    status: {
      success: "bg-success-bg border-success-border text-success-fg",
      warning: "bg-warning-bg border-warning-border text-warning-fg",
      error: "bg-error-bg border-error-border text-error-fg",
      info: "bg-info-bg border-info-border text-info-fg",
    },
  },
  defaultVariants: { status: "info" },
});

const STATUS_ICON = { success: CircleCheck, warning: TriangleAlert, error: CircleX, info: Info } as const;

type AlertBannerProps = {
  status?: Status;
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
};

export function AlertBanner({ status = "info", title, children, action, className = "" }: AlertBannerProps) {
  return (
    <div role="status" className={cn(banner({ status }), className)}>
      <Icon icon={STATUS_ICON[status]} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        {title && (
          <Text as="p" className="font-semibold text-current">
            {title}
          </Text>
        )}
        <Text as="p" size="sm" className="text-current">
          {children}
        </Text>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
